/**
 * useTalentMobilityData.js
 *
 * Every Talent Mobility screen reads through these hooks instead of importing
 * mockData directly. There is no live backend yet — swapping in real API
 * calls later means changing the body of these hooks only, not the screens
 * that consume them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchSavedTalentMobilityCareerPaths,
  getEmployeeSummary,
  getTalentMobilityEmployees,
  getTalentMobilityEmployeeSkills,
  getTalentMobilityJdActiveEmployees,
  getTalentMobilityNextRoles,
  getTalentMobilityPeopleProfileDetails,
  getTalentMobilityRoleSuccessorAnalytics,
  getTalentMobilityWorkforcePeopleAvailability,
  updateTalentMobilityJobOpeningStatus,
} from "Api/apiService";
import { SbNotification } from "uiCore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import useTalentMobilityScopeStore from "Store/useTalentMobilityScopeStore";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import {
  ROLES,
  PEOPLE,
  ROLE_OCCUPANCY,
  PATHWAYS,
  getPeopleForRole,
  getRolesForPerson,
  getSkillGap,
  getCriticalSkillGaps,
  getCandidateProfile,
  getOpportunity,
  computeMatchForRole,
  CURRENT_EMPLOYEE_ID,
} from "./mockData/talentMobility.mock";
import {
  JOB_OPENING_STATUS,
  ROLE_STATUS_OPEN,
  SCOPE_FULL_ORG,
  SCOPE_MY_TEAM,
  SUCCESSOR_TOP_N_DEFAULT,
} from "./talentMobility.constants";
import {
  mapEmployeesResponseToRedeploymentPeople,
  mapEmployeesResponseToRedeploymentRoles,
  mapEmployeesResponseToTableRows,
} from "./talentMobilityEmployees.utils";

// Line Manager's mock "own team" — arbitrary but stable for the demo.
const MOCK_TEAM_NAME = "Engineering — Platform";

const deriveScopeFromRole = (profileRole) =>
  /line manager/i.test(profileRole || "") ? SCOPE_MY_TEAM : SCOPE_FULL_ORG;

// ─── Scope toggle — defaults from the real logged-in profileRole ─────────────
// Backed by a single shared store (useTalentMobilityScopeStore) so every
// consumer (this hook, useTalentMobilityRoles, useRedeploymentCandidates)
// sees the same value — previously each call site had its own independent
// useState, so toggling in one place silently didn't affect the others.
// With no manual override, scope is derived from profileRole: any profile
// named "Line Manager" (case-insensitive) defaults to "My Team", everyone
// else defaults to full org — there's still no real PAIC-style org-scoping
// data behind this, it's a same team-name stand-in either way.

export const useScopeToggle = () => {
  const profileRole = useEntitlementsStore((state) => state.profileRole);
  const manualScope = useTalentMobilityScopeStore((state) => state.manualScope);
  const setManualScope = useTalentMobilityScopeStore((state) => state.setManualScope);
  const clearManualScope = useTalentMobilityScopeStore((state) => state.clearManualScope);

  const scope = manualScope || deriveScopeFromRole(profileRole);
  const isMyTeamOnly = scope === SCOPE_MY_TEAM;
  const isAutoFromRole = !manualScope;

  return {
    scope,
    setScope: setManualScope,
    resetToRoleDefault: clearManualScope,
    isAutoFromRole,
    isMyTeamOnly,
    myTeamName: MOCK_TEAM_NAME,
    profileRole,
  };
};

// ─── Employees list (landing / "starting point") ──────────────────────────────
// Rows come solely from GET /api/talent-mobility/employees — the landing page
// shows real records only (the transitional mock-PEOPLE-first mix was removed).
const TalentMobilityEmployeesStatus = Object.freeze({
  LOADING: "loading",
  IDLE: "idle",
});

const JOB_STATUS_SAVE_DEBOUNCE_MS = 500;

// Maps one employee row to the PUT /talent-mobility/job-opening-status body
// — sb_unique_id + user_id scope the change to this one employee's
// assignment row (others sharing the JD are untouched). Always sends BOTH
// flags, not just whichever checkbox changed: open_users_global_plans
// requires both columns non-NULL, and the INSERT that creates a brand-new
// row only ever gets the columns this payload carries — so the row's own
// current (post-optimistic-update) values for both stand in for "the field
// that didn't change" rather than leaving it unset on a first-time toggle.
const toJobOpeningStatusPayload = (roleId, employeeId, row) => ({
  sb_unique_id: roleId,
  user_id: employeeId,
  job_opening_status: row.status === ROLE_STATUS_OPEN ? JOB_OPENING_STATUS.OPEN : JOB_OPENING_STATUS.CLOSED,
  is_job_discoverable: Boolean(row.discoverable),
});

export const useTalentMobilityEmployees = () => {
  const [apiEmployees, setApiEmployees] = useState([]);
  const [status, setStatus] = useState(TalentMobilityEmployeesStatus.LOADING);
  const [error, setError] = useState(null);

  // Mirror of apiEmployees for reads inside updateRoleField without adding it
  // to the callback's deps (which would recreate the callback — and rebuild
  // the table columns memo — on every optimistic row update).
  const apiEmployeesRef = useRef([]);
  useEffect(() => {
    apiEmployeesRef.current = apiEmployees;
  }, [apiEmployees]);

  // One pending save per roleId+field, so rapid toggles of one checkbox
  // collapse into a single PUT while different checkboxes never cancel each
  // other. Same ref + setTimeout debounce as SbJobsLibrary's search box.
  // savedValuesRef holds the last server-acknowledged value per key so a
  // failed save can revert the optimistic update.
  const pendingSavesRef = useRef({});
  const savedValuesRef = useRef({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    // Flush (not drop) pending saves on unmount — a checkbox toggle followed
    // by a quick row-click navigation would otherwise silently lose the change.
    return () => {
      isMountedRef.current = false;
      Object.values(pendingSavesRef.current).forEach(({ timerId, run }) => {
        clearTimeout(timerId);
        run();
      });
      pendingSavesRef.current = {};
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadEmployees = async () => {
      try {
        const response = await getTalentMobilityEmployees();
        if (!isMounted) return;
        setApiEmployees(mapEmployeesResponseToTableRows(response));
        setError(null);
      } catch (fetchError) {
        if (isMounted) setError(fetchError);
      } finally {
        if (isMounted) setStatus(TalentMobilityEmployeesStatus.IDLE);
      }
    };

    loadEmployees();
    return () => {
      isMounted = false;
    };
  }, []);

  // Optimistic update keyed per employee row, so toggling one employee's
  // checkbox never touches other employees who share the same JD. Rows
  // persist via a debounced PUT /talent-mobility/job-opening-status
  // (sb_unique_id + user_id), reverted on failure.
  const updateRoleField = useCallback((employeeId, roleId, field, value) => {
    setApiEmployees((previous) =>
      previous.map((employee) => (employee.id === employeeId ? { ...employee, [field]: value } : employee))
    );

    // The ref still holds the pre-toggle rows here (it syncs after render),
    // which is exactly what the first-save revert value needs.
    const apiRow = apiEmployeesRef.current.find((employee) => employee.id === employeeId);
    if (!apiRow) return;

    const key = `${employeeId}|${field}`;
    if (!(key in savedValuesRef.current)) savedValuesRef.current[key] = apiRow[field];

    const pendingSave = pendingSavesRef.current[key];
    if (pendingSave) clearTimeout(pendingSave.timerId);

    const run = async () => {
      delete pendingSavesRef.current[key];
      try {
        // Read the row fresh at save time (not the pre-toggle `apiRow`
        // above) — by now the optimistic update has landed, and if the
        // OTHER checkbox was also toggled while this save was debouncing,
        // this picks up its latest value too instead of overwriting it
        // with a stale one.
        const latestRow = apiEmployeesRef.current.find((employee) => employee.id === employeeId) || apiRow;
        await updateTalentMobilityJobOpeningStatus(toJobOpeningStatusPayload(roleId, employeeId, latestRow));
        savedValuesRef.current[key] = value;
      } catch (saveError) {
        if (!isMountedRef.current) return;
        const lastSavedValue = savedValuesRef.current[key];
        setApiEmployees((previous) =>
          previous.map((employee) => (employee.id === employeeId ? { ...employee, [field]: lastSavedValue } : employee))
        );
        SbNotification.error(
          "Unable to save change",
          saveError?.response?.data?.message || "The change has been reverted. Please try again."
        );
      }
    };

    pendingSavesRef.current[key] = { timerId: setTimeout(run, JOB_STATUS_SAVE_DEBOUNCE_MS), run };
  }, []);

  return {
    employees: apiEmployees,
    updateRoleField,
    isLoading: status === TalentMobilityEmployeesStatus.LOADING,
    error,
  };
};

// ─── Role Detail — successors / match ────────────────────────────────────────

// people_profiles.critical_skills has no enforced schema (the analytics
// endpoint returns it "as stored"), so tolerate both plain strings and
// objects and hand PrioritySkillTags the {skillId, skillName} shape it needs.
// Entries that can't be normalized are dropped with a console.warn (same
// pattern as useSuccessionTree's warnings) so a backend data-quality problem
// is visible instead of skills silently missing from a list.
//
// role-successor-analytics is the one caller whose entries also carry this
// specific candidate's proficiency_needed/profile_proficiency for that
// skill (their own gap among the role's top skills by importance) — every
// other caller (next-roles, jd-active-employees) only ever sends a name, so
// `required`/`current` stay undefined for those and PrioritySkillTags
// renders exactly as it did before this was added.
const normalizeCriticalSkills = (criticalSkills) => {
  if (criticalSkills != null && !Array.isArray(criticalSkills)) {
    console.warn("Talent Mobility: dropped non-array critical_skills:", criticalSkills);
    return [];
  }
  return (criticalSkills || [])
    .map((skill, index) => {
      if (typeof skill === "string") return { skillId: `${index}-${skill}`, skillName: skill };
      const skillName = skill?.skillName || skill?.skill_name || skill?.skill || skill?.name;
      if (!skillName) {
        console.warn("Talent Mobility: dropped critical skill with no name:", skill);
        return null;
      }
      const required = skill?.proficiency_needed;
      const current = skill?.profile_proficiency;
      return {
        skillId: skill?.skillId || skill?.skill_id || `${index}-${skillName}`,
        skillName,
        ...(required != null && current != null ? { required, current } : {}),
      };
    })
    .filter(Boolean);
};

// Maps a POST /talent-mobility/role-successor-analytics response to the
// candidate row shape RoleDetailV2's table renders (same shape as the mock
// getPeopleForRole rows).
const mapSuccessorAnalyticsToCandidates = (response) => {
  const employees = Array.isArray(response?.employees) ? response.employees : [];
  return employees.map((employee) => ({
    personId: employee.personProfileId,
    // null (never coerced to 0) when the candidate hasn't updated their
    // profile against this role yet — same "not scored" vs. "scored zero"
    // distinction as mapAvailabilityRecordToCandidate/mapNextRoleToRow.
    // RoleDetailV2's own SMI Match column renders this as a dash + tooltip.
    sri: employee.smi == null ? null : Math.round(employee.smi),
    // Display id is the HRIS emp_id and the candidate's own role is
    // current_role (this endpoint's other fields are camelCase, so tolerate
    // both spellings; nullable → "—"). The opaque personProfileId only
    // drives personId (row keys, skill-gap fetches).
    person: {
      employeeId: employee.emp_id ?? employee.empId ?? null,
      name: employee.name,
      currentRoleTitle: employee.current_role ?? employee.currentRole ?? null,
    },
    shortlisted: Boolean(employee.shortlisted),
    criticalGaps: normalizeCriticalSkills(employee.criticalSkills),
  }));
};

// `roleId` is either a mock ROLES id ("r-em-platform") or, for API-backed
// rows, a JD's jd_sb_unique_id. `occupantIds` is every current occupant the
// page was opened with (TalentMobilityManagerV2's Title link sends every
// employee whose position is Open for this role, or the whole roster if none
// are), and `role` is that same click's JD metadata (title/function/
// subFunction/location/level) — both travel as router state, not fetched
// here: TalentMobilityManagerV2 already has this data client-side (it's what
// built the row being clicked), so there's no need for this page to make its
// own POST /talent-mobility/people-profile-details round trip just to
// re-derive it.
//
// POST /talent-mobility/role-successor-analytics is still a real fetch —
// it's always sent the full occupantIds list as user_ids (the candidate pool
// excludes the teammates of ALL those occupants, not just their own JD).
// Each row's `shortlisted` flag comes back from the JD's own shared
// shortlist row (global_talentmobility_plans, one per jd_sb_unique_id — see
// shortlist_service on the backend), so every occupant of this role sees the
// same shortlisted candidates; there's no per-occupant scoping left to pass.
// `selectedOccupantId` is still exposed (the roster's first occupant) purely
// for the expanded row's "View Detailed Profile" link, which carries it as
// ?user_id= so the destination Candidate Profile page can exclude this
// role's own occupants' teammates from ITS OWN successor-analytics call —
// plain derived state, not its own useState/useEffect pair, since there's no
// occupant-switcher UI to ever set it to anything else; that used to fire
// this effect a second time on mount (once before the "reset" effect settled
// state, once after), doubling the analytics call for no reason.
export const useRoleSuccessors = (roleId, occupantIds = [], role = null) => {
  const [topN, setTopN] = useState(SUCCESSOR_TOP_N_DEFAULT);
  const occupantIdsKey = occupantIds.join(",");
  const selectedOccupantId = occupantIdsKey ? occupantIdsKey.split(",")[0] : null;

  const mockRole = useMemo(() => ROLES.find((r) => r.id === roleId) || null, [roleId]);

  // Fetch whenever the page was opened from a real row (`role` came as router
  // state) — even with no occupants: a role nobody holds (Open 0/0) still has
  // matched candidates, the backend just has no teammates to exclude. A
  // direct/bookmarked visit has no `role` and renders "Role not found", so it
  // skips the fetch.
  const hasRole = Boolean(role);
  const [apiCandidates, setApiCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(!mockRole && hasRole);

  useEffect(() => {
    if (mockRole || !hasRole) return undefined;
    // Re-split from the key (not the outer occupantIds closure, which is a
    // fresh [] every render whenever the caller passes no state) so this
    // effect's own dependency stays the stable, primitive occupantIdsKey.
    const ids = occupantIdsKey ? occupantIdsKey.split(",") : [];
    let isMounted = true;
    setIsLoadingCandidates(true);

    const loadCandidates = async () => {
      try {
        const response = await getTalentMobilityRoleSuccessorAnalytics(roleId, ids);
        if (!isMounted) return;
        setApiCandidates(mapSuccessorAnalyticsToCandidates(response));
      } catch (fetchError) {
        if (!isMounted) return;
        setApiCandidates([]);
        SbNotification.error(
          "Unable to load successors",
          fetchError?.response?.data?.message || "Please refresh the page to try again."
        );
      } finally {
        if (isMounted) setIsLoadingCandidates(false);
      }
    };

    loadCandidates();
    return () => {
      isMounted = false;
    };
  }, [mockRole, hasRole, roleId, occupantIdsKey]);

  // Ranked by SMI, highest first, before the Top N cut so the selection
  // always keeps the best matches. A null sri (profile not updated yet)
  // sorts last rather than being coerced to 0.
  const allCandidates = useMemo(() => {
    const source = mockRole ? getPeopleForRole(roleId) : apiCandidates;
    return [...source].sort((a, b) => (b.sri ?? -Infinity) - (a.sri ?? -Infinity));
  }, [mockRole, roleId, apiCandidates]);
  const candidates = useMemo(() => allCandidates.slice(0, topN), [allCandidates, topN]);

  return {
    role: mockRole || role,
    selectedOccupantId: mockRole ? mockRole.occupantId : selectedOccupantId,
    candidates,
    totalCandidates: allCandidates.length,
    topN,
    setTopN,
    isLoading: isLoadingCandidates,
  };
};

// Like critical_skills, contextual_notes is stored schema-free — seen as a
// plain string but also as an array of notes. Flatten arrays into the
// " | "-separated string SkillDescriptionPopoverContent splits back into
// paragraphs; anything else is dropped (a non-string reaching the popover
// crashes on .split) — with a console.warn so the bad data is visible.
const toSkillDescription = (contextualNotes) => {
  if (typeof contextualNotes === "string") return contextualNotes || null;
  if (Array.isArray(contextualNotes)) {
    const droppedNotes = contextualNotes.filter((note) => typeof note !== "string");
    if (droppedNotes.length) {
      console.warn("Talent Mobility: dropped non-string contextual_notes entries:", droppedNotes);
    }
    const notes = contextualNotes.filter((note) => typeof note === "string" && note.trim());
    return notes.length ? notes.join(" | ") : null;
  }
  if (contextualNotes != null) {
    console.warn("Talent Mobility: dropped non-string contextual_notes:", contextualNotes);
  }
  return null;
};

// Maps a GET /talent-mobility/employee-skills response to the row shape
// SkillGapList renders. Same field names as SkillsBridger's own skill views
// (see transformSkillsForProficiency in EmployeeSkillsMapV2.constants.js).
const mapEmployeeSkillsToSkillGap = (response) => {
  const skills = Array.isArray(response?.skills) ? response.skills : [];
  return skills.map((skill, index) => ({
    skillId: skill.skill_id || `${index}-${skill.skill}`,
    skillName: skill.skill,
    description: toSkillDescription(skill.contextual_notes),
    required: skill.proficiency_needed ?? 0,
    current: skill.profile_proficiency ?? 0,
    // importance is its own independent 1-5 rating (5 = "Critical") on how
    // important the skill is to the role — distinct from proficiency_needed
    // (how skilled someone must be). Same rule the critical-skills gap list
    // uses (skill_gaps_for_candidate on the backend); SkillGapList reads
    // this to star the skill name.
    priority: Number(skill.importance) === 5,
  }));
};

// Mock (role, person) pairs resolve synchronously from ROLE_SKILLS; pairs on
// an API-backed role fetch GET /talent-mobility/employee-skills instead. A
// 404 means "no profile row for this person + JD" — an expected data state
// rendered as SkillGapList's own empty message, not an error notification.
// Alongside the skills, the API response also carries the JD's job_title and
// the pair's profile_score — surfaced as roleTitle/matchPct so the Candidate
// Profile header can label the gap without a second request (both are null
// for mock pairs, whose title/match come from the mock data instead).
export const useSkillGapForPair = (roleId, personId) => {
  const isMockRole = useMemo(() => ROLES.some((r) => r.id === roleId), [roleId]);
  const mockSkillGap = useMemo(
    () => (isMockRole ? getSkillGap(roleId, personId) : []),
    [isMockRole, roleId, personId]
  );

  const [apiSkillGap, setApiSkillGap] = useState([]);
  const [apiRoleContext, setApiRoleContext] = useState(null); // { roleTitle, matchPct }
  const [isLoading, setIsLoading] = useState(!isMockRole && Boolean(roleId && personId));

  useEffect(() => {
    if (isMockRole || !roleId || !personId) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadSkills = async () => {
      try {
        const response = await getTalentMobilityEmployeeSkills(personId, roleId);
        if (!isMounted) return;
        setApiSkillGap(mapEmployeeSkillsToSkillGap(response));
        setApiRoleContext({
          roleTitle: response?.job_title || null,
          matchPct: response?.profile_score == null ? null : Math.round(response.profile_score),
        });
      } catch (fetchError) {
        if (!isMounted) return;
        setApiSkillGap([]);
        setApiRoleContext(null);
        if (fetchError?.response?.status !== 404) {
          SbNotification.error(
            "Unable to load skills",
            fetchError?.response?.data?.message || "Please refresh the page to try again."
          );
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadSkills();
    return () => {
      isMounted = false;
    };
  }, [isMockRole, roleId, personId]);

  return {
    skillGap: isMockRole ? mockSkillGap : apiSkillGap,
    roleTitle: isMockRole ? null : apiRoleContext?.roleTitle ?? null,
    matchPct: isMockRole ? null : apiRoleContext?.matchPct ?? null,
    isLoading,
  };
};

// The caller's saved career-paths response — POST /career-paths with an
// EMPTY payload, which makes the backend look up the signed-in user's stored
// aspiration (talent_mobility_aspiration) and return its pathway plus
// target_role_id / target_role_title / locations. `saved` stays null both
// when nothing was ever saved (fetchSavedTalentMobilityCareerPaths' 404
// case) and while loading — `error` is what tells those apart from a real
// hydration failure (5xx/timeout), since apiService only throws for the
// latter. SkillGapDetailV2 ignores `error` and keeps falling back to its own
// /employee-skills header fields either way; TalentMobilityEmployeeV2 uses it
// to avoid showing the blank capture form (and risking an overwrite of the
// real saved aspiration) when hydration merely failed rather than found
// nothing. Pass enabled=false to skip the call entirely (e.g. the detail
// page already got the fields via navigation state).
export const useSavedCareerPaths = (enabled = true) => {
  const [saved, setSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchSaved = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(false);
    try {
      const response = await fetchSavedTalentMobilityCareerPaths();
      if (requestId !== requestIdRef.current) return;
      setSaved(response);
    } catch {
      // Already logged by apiService; a genuine 404 resolves above instead
      // of throwing, so reaching here always means a real failure.
      if (requestId !== requestIdRef.current) return;
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchSaved();
    // Invalidate any in-flight request from this effect instance so its
    // response can't land after a re-render with enabled=false.
    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, fetchSaved]);

  return { saved, isLoading, error, refetch: fetchSaved };
};

export const useCriticalSkillGaps = (roleId, personId) =>
  useMemo(() => getCriticalSkillGaps(roleId, personId), [roleId, personId]);

// Match % + shortlisted flag for one (role, candidate) pair, for the
// Candidate Profile header. Mock ROLES ids keep the mock computeMatchForRole
// value (temporary, until those screens are API-backed). API-backed roles
// read the same POST /talent-mobility/role-successor-analytics rows Role
// Detail lists, sending the occupant userId carried in the URL (?user_id=...)
// as the sole entry of user_ids so the candidate pool's teammate-exclusion
// matches what Role Detail itself excluded. `shortlisted` comes back from
// the JD's own shared shortlist row (one per role, not per occupant), so it
// agrees with Role Detail's table regardless of which occupant's link this
// page was opened from. No occupant id (a role nobody holds) sends an empty
// user_ids, same as Role Detail. A failed fetch or no row for this
// candidate leaves matchPct null so the page hides the tag instead of
// showing a mock-derived 0%. `enabled` lets the caller skip this
// BigQuery-heavy read on entry points that never use its result (flows
// other than Role Detail).
export const useSuccessorMatchForPair = (roleId, occupantUserId, personId, enabled = true) => {
  const isMockRole = useMemo(() => ROLES.some((r) => r.id === roleId), [roleId]);
  const mockMatch = useMemo(
    () => (isMockRole ? computeMatchForRole(roleId, personId) : null),
    [isMockRole, roleId, personId]
  );

  const shouldFetch = enabled && !isMockRole && Boolean(roleId && personId);
  const [apiMatch, setApiMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadMatch = async () => {
      try {
        const response = await getTalentMobilityRoleSuccessorAnalytics(roleId, occupantUserId ? [occupantUserId] : []);
        if (!isMounted) return;
        const employees = Array.isArray(response?.employees) ? response.employees : [];
        const employee = employees.find((e) => e.personProfileId === personId);
        setApiMatch(
          employee
            ? {
                matchPct: employee.smi == null ? null : Math.round(employee.smi),
                shortlisted: Boolean(employee.shortlisted),
              }
            : null
        );
      } catch (fetchError) {
        if (isMounted) setApiMatch(null); // quiet — the tag just doesn't render
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadMatch();
    return () => {
      isMounted = false;
    };
  }, [shouldFetch, roleId, occupantUserId, personId]);

  if (isMockRole) return { matchPct: mockMatch?.matchPct ?? null, apiShortlisted: false, isLoading: false };
  // A fetch cut off by shouldFetch turning false never reaches its finally,
  // so gate isLoading on it — the Shortlist button must not spin forever.
  return {
    matchPct: apiMatch?.matchPct ?? null,
    apiShortlisted: Boolean(apiMatch?.shortlisted),
    isLoading: shouldFetch && isLoading,
  };
};

// ─── Candidate Profile detail view ────────────────────────────────────────────

// "Mar 2024" — previous-role date ranges on the profile card.
const formatRoleDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

// Maps a POST /talent-mobility/people-profile-details response to the profile
// shape CandidateProfileCard renders — same shape the mock
// getCandidateProfile returns, so the page can't tell the sources apart.
// `augmentationPotential` feeds the card's "Current SRI" bar.
const mapProfileDetailsToCandidateProfile = (details) => {
  if (!details?.userId) return null;
  const currentRole = details.currentRole;
  const metadata = currentRole?.jdMetadataJson || {};
  return {
    id: details.userId,
    // Display id is the HRIS emp_id (nullable — renderers hide it then);
    // the opaque userId only drives `id`.
    employeeId: details.emp_id ?? null,
    name: details.name,
    currentRoleTitle: currentRole?.roleName || "—",
    // Not rendered by CandidateProfileCard — carried for useEmployeeInsights'
    // employee_summary payload (same fields EmployeeSkillsMapV2 sends).
    businessUnit: metadata.business_unit || "",
    function: metadata.function || "",
    jobGrade: metadata.level || "",
    joiningDate: currentRole?.joiningDate || null,
    augmentationPotential: currentRole?.sri == null ? null : Math.round(currentRole.sri),
    previousRoles: (details.previousRoles || []).map((previousRole) => ({
      title: previousRole.roleName || "—",
      dateRange: [formatRoleDate(previousRole.startDate), formatRoleDate(previousRole.endDate)]
        .filter(Boolean)
        .join(" – "),
    })),
    contact: {
      location: metadata.location || null,
      email: details.email || null,
    },
  };
};

// Mock PEOPLE first (hand-authored "p-*" ids), then POST
// /talent-mobility/people-profile-details for real user ids — mirrors
// useRoleSuccessors' mock-first split so both detail pages resolve the same
// ids the same way.
export const useCandidateProfile = (personId) => {
  const mockProfile = useMemo(() => getCandidateProfile(personId), [personId]);

  const [apiProfile, setApiProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(!mockProfile && Boolean(personId));

  useEffect(() => {
    if (mockProfile || !personId) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadProfile = async () => {
      try {
        const details = await getTalentMobilityPeopleProfileDetails({ user_id: personId });
        if (isMounted) setApiProfile(mapProfileDetailsToCandidateProfile(details));
      } catch (fetchError) {
        if (!isMounted) return;
        setApiProfile(null);
        SbNotification.error(
          "Unable to load candidate",
          fetchError?.response?.data?.message || "Please refresh the page to try again."
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, [mockProfile, personId]);

  return { profile: mockProfile || apiProfile, isLoading };
};

// "Individual Profile Summary" panel — mirrors EmployeeSkillsMapV2's
// employee_summary fetch (same POST /skillsbridger/hr/employee_summary, same
// ai_generated_summary.employee_summary read) so both detailed profile views
// produce identical summaries for the same person. The backend derives the
// summary from employee_id alone (the route ignores any profile fields in
// the body), so the fetch fires as soon as the page knows the person id — in
// parallel with people-profile-details and employee-skills, not after them.
// `precomputedInsights` short-circuits the fetch for mock candidates, whose
// hand-authored aiSummary ships inline. Failures are quiet — the hook returns
// null insights and the panel falls back to its no-data message.
export const useEmployeeInsights = (personId, precomputedInsights = null) => {
  const [apiInsights, setApiInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!personId || precomputedInsights) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadInsights = async () => {
      try {
        const data = await getEmployeeSummary(personId);
        if (isMounted) setApiInsights(data?.ai_generated_summary?.employee_summary ?? null);
      } catch (fetchError) {
        if (isMounted) setApiInsights(null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadInsights();
    return () => {
      isMounted = false;
    };
  }, [personId, precomputedInsights]);

  return { insights: precomputedInsights ?? apiInsights, isLoading };
};

// ─── Succession Planning — org tree ───────────────────────────────────────────

const buildOrgTree = (roles) => {
  const byId = Object.fromEntries(roles.map((r) => [r.id, { ...r, children: [] }]));
  const roots = [];
  roles.forEach((r) => {
    if (r.reportsToRoleId && byId[r.reportsToRoleId]) {
      byId[r.reportsToRoleId].children.push(byId[r.id]);
    } else {
      roots.push(byId[r.id]);
    }
  });
  return roots;
};

// Keeps a role's full ancestor chain even if the ancestor itself doesn't
// match the filter, so a filtered-to sub-org still renders as a connected
// tree rooted at the top instead of orphaned branches. Each filter is a
// multi-select array — an empty array means "any" for that field.
const applyOrgFilters = (roles, filters) => {
  const hasAnyFilter = Object.values(filters).some((v) => v?.length > 0);
  if (!hasAnyFilter) return roles;

  const byId = Object.fromEntries(roles.map((r) => [r.id, r]));
  const matches = (r) =>
    (!filters.legalEntity?.length || filters.legalEntity.includes(r.legalEntity)) &&
    (!filters.businessUnit?.length || filters.businessUnit.includes(r.businessUnit)) &&
    (!filters.function?.length || filters.function.includes(r.function)) &&
    (!filters.subFunction?.length || filters.subFunction.includes(r.subFunction)) &&
    (!filters.level?.length || filters.level.includes(r.level)) &&
    (!filters.location?.length || filters.location.includes(r.location));

  const keepIds = new Set();
  roles.filter(matches).forEach((r) => {
    let current = r;
    while (current) {
      keepIds.add(current.id);
      current = current.reportsToRoleId ? byId[current.reportsToRoleId] : null;
    }
  });
  return roles.filter((r) => keepIds.has(r.id));
};

export const useOrgTree = (filters = {}) => {
  const { isMyTeamOnly, myTeamName } = useScopeToggle();
  const { green: matchThreshold } = useMatchThresholds();

  const tree = useMemo(() => {
    // Succession Planning is always org-wide (req #12) — the scope toggle
    // only affects the Roles List / Redeployment Center, not this view.
    // The Legal Entity/BU/Function/Sub-Function row lets a user flip to a
    // specific part of that same org-wide tree instead.
    void isMyTeamOnly;
    void myTeamName;
    return buildOrgTree(applyOrgFilters(ROLES, filters));
  }, [isMyTeamOnly, myTeamName, filters]);

  const getBoxData = useCallback(
    (roleId) => {
      const role = ROLES.find((r) => r.id === roleId);
      const occupancy = ROLE_OCCUPANCY[roleId] || {};
      const occupantName = PEOPLE.find((p) => p.id === role?.occupantId)?.name || "Vacant";
      const allMatches = getPeopleForRole(roleId);
      const successors = allMatches.slice(0, 3);
      const matchCount = allMatches.filter((m) => m.sri >= matchThreshold).length;
      return { role, occupancy, occupantName, successors, matchCount };
    },
    [matchThreshold]
  );

  return { tree, getBoxData };
};

// ─── Redeployment Center — bidirectional (Role View / Job View, req #10) ─────
// Both views are fed by GET /talent-mobility/employees only — no mock demo
// rows: Employee View lists the response one row per person, Role View
// groups it by JD (one row per role, currentPositionCount = live occupant
// count) — see the two mappers in talentMobilityEmployees.utils.js.
// Role/people rows both expose the same six filter fields (legalEntity,
// businessUnit, function, subFunction, level, location) so the top filter
// row works identically regardless of which view is active. Workforce
// Planning shares this hook for its demand roles list.

export const useRedeploymentCandidates = () => {
  const [apiPeople, setApiPeople] = useState([]);
  const [apiRoles, setApiRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadEmployees = async () => {
      try {
        const response = await getTalentMobilityEmployees();
        if (!isMounted) return;
        setApiPeople(mapEmployeesResponseToRedeploymentPeople(response));
        setApiRoles(mapEmployeesResponseToRedeploymentRoles(response));
      } catch (fetchError) {
        if (!isMounted) return;
        setApiPeople([]);
        setApiRoles([]);
        SbNotification.error(
          "Unable to load employees",
          fetchError?.response?.data?.message || "Please refresh the page to try again."
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadEmployees();
    return () => {
      isMounted = false;
    };
  }, []);

  return { people: apiPeople, roles: apiRoles, isLoading };
};

// Maps one POST /talent-mobility/next-roles row to the shape Employee View's
// nested table renders — same fields as the mock getRolesForPerson rows,
// plus criticalGaps read straight off the response.
const mapNextRoleToRow = (nextRole) => ({
  roleId: nextRole.jd_sb_unique_id,
  // null (never coerced to 0) when the person hasn't updated their profile
  // yet — same "not scored" vs. "scored zero" distinction as
  // mapAvailabilityRecordToCandidate above. The role itself is still a real
  // potential next role worth showing (unlike an unscored Workforce Planning
  // candidate), so PersonNextRolesTable's SMI Match column renders this as a
  // dash + tooltip rather than dropping the row — same convention
  // RoleDetailV2's own SMI column already uses for a 0 it gets from a
  // different endpoint.
  sri: nextRole.profile_score == null ? null : Math.round(nextRole.profile_score),
  role: {
    title: nextRole.role_name,
    function: nextRole.jd_metadata_json?.function,
    // The person has an ENDED employee_jd_assignment against this JD —
    // they've held it before, so the UI labels it "Past" instead of
    // presenting it as a fresh match.
    isPast: Boolean(nextRole.is_past),
  },
  criticalGaps: normalizeCriticalSkills(nextRole.critical_skills),
});

// One person's next roles, fetched and mapped to row shape. Shared by the
// lazy per-row hook below and one-shot callers (e.g. the Reduction Detail
// CSV export, which needs every occupant's roles without expanding rows).
export const fetchNextRolesForPersonId = async (personId) => {
  const response = await getTalentMobilityNextRoles(personId);
  const nextRoles = Array.isArray(response?.nextRoles) ? response.nextRoles : [];
  return nextRoles.map(mapNextRoleToRow);
};

// Candidate next roles for one employee. Mock people carry their rows
// precomputed (person.nextRoles is an array); API-backed people have
// nextRoles: null — "not loaded" — and fetch POST /talent-mobility/next-roles
// lazily, i.e. only once their row is expanded and this hook mounts.
export const useNextRolesForPerson = (person) => {
  const precomputedNextRoles = person?.nextRoles ?? null;
  const shouldFetch = precomputedNextRoles == null && Boolean(person?.id);

  const [apiNextRoles, setApiNextRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadNextRoles = async () => {
      try {
        const nextRoles = await fetchNextRolesForPersonId(person.id);
        if (!isMounted) return;
        setApiNextRoles(nextRoles);
      } catch (fetchError) {
        if (!isMounted) return;
        setApiNextRoles([]);
        SbNotification.error(
          "Unable to load next roles",
          fetchError?.response?.data?.message || "Please try again."
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadNextRoles();
    return () => {
      isMounted = false;
    };
  }, [shouldFetch, person?.id]);

  return { nextRoles: precomputedNextRoles ?? apiNextRoles, isLoading };
};

// ─── Redeployment reduction workflow — role's current occupants, ranked ─────
// by SMI (their own fit for the role they're already in — the closest stand-
// in for "who's strongest in-role" this mock data has, since there's no
// separate performance metric).

// Reduction Detail — the role's occupants, ranked by score (highest first,
// so "below the target headcount" = at risk). Mock ROLES ids resolve
// synchronously from PEOPLE/MATCH_PAIRS; API-backed roles fetch
// GET /talent-mobility/jd-active-employees (the JD's ACTIVE assignees with
// their people_profiles score). That endpoint carries no JD metadata, so the
// API role header is built from the roleId plus the title the Role View link
// carries over (?title=...), with currentPositionCount = the occupant count.
// Each occupant's person carries nextRoles the way the Redeployment rows do:
// a precomputed array for mock people, null ("load lazily via
// useNextRolesForPerson") for API people.
export const useRoleReductionDetail = (roleId, roleTitle) => {
  const mockRole = useMemo(() => ROLES.find((r) => r.id === roleId) || null, [roleId]);
  const mockOccupants = useMemo(() => {
    if (!mockRole) return [];
    return PEOPLE.filter((p) => p.currentRoleId === roleId)
      .map((p) => ({
        ...computeMatchForRole(roleId, p.id),
        person: { ...p, nextRoles: getRolesForPerson(p.id) },
      }))
      .sort((a, b) => b.matchPct - a.matchPct);
  }, [mockRole, roleId]);

  const [apiOccupants, setApiOccupants] = useState([]);
  const [isLoading, setIsLoading] = useState(!mockRole && Boolean(roleId));

  useEffect(() => {
    if (mockRole || !roleId) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadOccupants = async () => {
      try {
        const response = await getTalentMobilityJdActiveEmployees(roleId);
        if (!isMounted) return;
        const employees = Array.isArray(response?.employees) ? response.employees : [];
        setApiOccupants(
          employees
            .map((employee) => ({
              // null (never coerced to 0) when this occupant hasn't updated
              // their profile against this role yet — same "not scored" vs.
              // "scored zero" distinction used elsewhere in this file.
              // buildScoreColumn's shared SMI/SRI render falls back to a
              // dash + tooltip for a null value.
              matchPct: employee.profile_score == null ? null : Math.round(employee.profile_score),
              criticalGaps: normalizeCriticalSkills(employee.critical_skills),
              person: {
                id: employee.userId,
                // Display id is the HRIS emp_id (nullable → "—"); the opaque
                // userId stays on `id` for plan actions and row keys.
                employeeId: employee.emp_id ?? null,
                name: employee.name,
                nextRoles: null,
              },
            }))
            .sort((a, b) => b.matchPct - a.matchPct)
        );
      } catch (fetchError) {
        if (!isMounted) return;
        setApiOccupants([]);
        SbNotification.error(
          "Unable to load role occupants",
          fetchError?.response?.data?.message || "Please refresh the page to try again."
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadOccupants();
    return () => {
      isMounted = false;
    };
  }, [mockRole, roleId]);

  const apiRole = useMemo(
    () => ({ id: roleId, title: roleTitle || roleId, currentPositionCount: apiOccupants.length }),
    [roleId, roleTitle, apiOccupants.length]
  );

  return mockRole
    ? { role: mockRole, occupants: mockOccupants, isLoading: false }
    : { role: apiRole, occupants: apiOccupants, isLoading };
};

// ─── Workforce Planning — people availability per demand role ───────────────

// The endpoint returns people pre-split by availability:
//   { jd_sb_unique_id, available_users: [{ userId, emp_id, name,
//     profile_score, critical_skills }], unavailable_users: [...] }
// Each record maps to the CandidateRow shape Workforce Planning renders,
// with `available` set from which list the record came in. The displayed
// employeeId is the HRIS emp_id (nullable → rows render "—"); the opaque
// userId only drives personId (navigation, shortlisting, row keys).
// sri stays null (never coerced to 0) when the backend has no real score for
// this person yet — profile_score/smi both missing means "not scored", not
// "scored zero". Every renderer of this sri (WorkforcePlanningV2's
// CandidateRow, its CSV export, ViewPlanningV2's WorkforceJobDetail) shows a
// dash + "User has not updated profile" tooltip instead of a percentage —
// same convention as RoleDetailV2's own SMI column and the Next Roles
// panels — rather than dropping the candidate from the list entirely.
const mapAvailabilityRecordToCandidate = (record, available) => {
  const personId = record.userId ?? record.user_id ?? record.employee_unique_id;
  const score = record.profile_score ?? record.smi;
  return {
    personId,
    sri: score == null ? null : Math.round(score),
    person: { id: personId, employeeId: record.emp_id ?? null, name: record.name },
    available,
  };
};

const mapAvailabilityResponseToCandidates = (response) => [
  ...(Array.isArray(response?.available_users) ? response.available_users : []).map((record) =>
    mapAvailabilityRecordToCandidate(record, true)
  ),
  ...(Array.isArray(response?.unavailable_users) ? response.unavailable_users : []).map((record) =>
    mapAvailabilityRecordToCandidate(record, false)
  ),
];

// Candidates + availability for one Workforce Planning demand role, each row
// carrying an `available` flag the page splits its A/B sections on. Mock
// roles keep their precomputed matched candidates, flagged from the session
// availability store (markAvailable in the redeployment flow); API-backed
// roles fetch GET /talent-mobility/workforce-planning/people-availability
// when the Candidates step opens — there the backend owns availability (from
// submitted redeployment plans), so the flag comes straight off the response.
export const useWorkforcePeopleAvailability = (role) => {
  const isMockRole = useMemo(() => ROLES.some((r) => r.id === role?.id), [role?.id]);
  const availableMap = useAvailabilityStore((state) => state.available);

  const mockCandidates = useMemo(
    () =>
      isMockRole
        ? (role?.candidates ?? []).map((candidate) => ({
            ...candidate,
            available: Boolean(availableMap[candidate.personId]),
          }))
        : [],
    [isMockRole, role, availableMap]
  );

  const [apiCandidates, setApiCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(!isMockRole && Boolean(role?.id));

  useEffect(() => {
    if (isMockRole || !role?.id) return undefined;
    let isMounted = true;
    setIsLoading(true);

    const loadAvailability = async () => {
      try {
        const response = await getTalentMobilityWorkforcePeopleAvailability(role.id);
        if (!isMounted) return;
        setApiCandidates(mapAvailabilityResponseToCandidates(response));
      } catch (fetchError) {
        if (!isMounted) return;
        setApiCandidates([]);
        SbNotification.error(
          "Unable to load people availability",
          fetchError?.response?.data?.message || "Please try again."
        );
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadAvailability();
    return () => {
      isMounted = false;
    };
  }, [isMockRole, role?.id]);

  return { candidates: isMockRole ? mockCandidates : apiCandidates, isLoading };
};

// Candidates + availability for EVERY demand role at once — the Summary step
// and CSV export need per-role matched/available data, including API-backed
// roles whose candidates only exist behind
// GET /talent-mobility/workforce-planning/people-availability (the Candidates
// step fetches them one selected role at a time, so `role.candidates` stays
// empty for API roles). Mock roles resolve synchronously from their
// precomputed candidates + the session availability store; API roles fetch
// once per role id, only while `enabled` (the page passes "Summary step is
// open"), cached for the page's lifetime.
export const useWorkforceDemandCandidates = (demandRoles, enabled) => {
  const availableMap = useAvailabilityStore((state) => state.available);
  const [apiCandidatesByRoleId, setApiCandidatesByRoleId] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  // Ids already requested this page visit — fetches resolve out of order, so
  // the guard can't be the results map itself.
  const requestedRoleIdsRef = useRef(new Set());

  const apiRoleIds = useMemo(
    () =>
      demandRoles
        .filter((role) => !ROLES.some((mockRole) => mockRole.id === role.id))
        .map((role) => role.id),
    [demandRoles]
  );

  useEffect(() => {
    if (!enabled) return;
    const missingIds = apiRoleIds.filter((roleId) => !requestedRoleIdsRef.current.has(roleId));
    if (!missingIds.length) return;
    missingIds.forEach((roleId) => requestedRoleIdsRef.current.add(roleId));
    setPendingCount((prev) => prev + missingIds.length);

    missingIds.forEach(async (roleId) => {
      let candidates = [];
      try {
        const response = await getTalentMobilityWorkforcePeopleAvailability(roleId);
        candidates = mapAvailabilityResponseToCandidates(response);
      } catch (fetchError) {
        SbNotification.error(
          "Unable to load people availability",
          fetchError?.response?.data?.message || "Please try again."
        );
      }
      setApiCandidatesByRoleId((prev) => ({ ...prev, [roleId]: candidates }));
      setPendingCount((prev) => Math.max(prev - 1, 0));
    });
  }, [enabled, apiRoleIds]);

  const candidatesByRoleId = useMemo(() => {
    const byRoleId = {};
    demandRoles.forEach((role) => {
      const isMockRole = ROLES.some((mockRole) => mockRole.id === role.id);
      byRoleId[role.id] = isMockRole
        ? (role.candidates ?? []).map((candidate) => ({
            ...candidate,
            available: Boolean(availableMap[candidate.personId]),
          }))
        : apiCandidatesByRoleId[role.id] ?? [];
    });
    return byRoleId;
  }, [demandRoles, availableMap, apiCandidatesByRoleId]);

  return { candidatesByRoleId, isLoading: pendingCount > 0 };
};

// ─── Employee — Career Pathways ──────────────────────────────────────────────

export const useCareerPathways = () => PATHWAYS;

// `opportunityId` is either a real PATHWAYS opportunity id, or — for
// off-pathway "other high matches" cards (no PATHWAYS entry) — a bare roleId
// used directly. Falling back to a roleId lookup means both card types can
// link to this same detail page without a second route.
export const useOpportunityDetail = (opportunityId) => {
  const opportunity = useMemo(() => getOpportunity(opportunityId), [opportunityId]);
  const role = useMemo(
    () => ROLES.find((r) => r.id === (opportunity ? opportunity.roleId : opportunityId)) || null,
    [opportunity, opportunityId]
  );
  const skillGap = useMemo(
    () => (role ? getSkillGap(role.id, CURRENT_EMPLOYEE_ID) : []),
    [role]
  );
  const { matchPct, skillsToDevelop } = useMemo(
    () => (role ? computeMatchForRole(role.id, CURRENT_EMPLOYEE_ID) : { matchPct: 0, skillsToDevelop: 0 }),
    [role]
  );

  return { opportunity, role, skillGap, matchPct, skillsToDevelop };
};

// Org-wide scan of Open + Discoverable roles for the mock current employee,
// used to surface "other high matches" beyond the pathway's own linked
// opportunities (req: green-shaded on-pathway cards vs. white off-pathway
// cards on the Career Pathways page).
export const useOpenPositionsForCurrentEmployee = () =>
  useMemo(
    () =>
      ROLES.filter((r) => r.status === "Open" && r.discoverable)
        .map((r) => ({ role: r, ...computeMatchForRole(r.id, CURRENT_EMPLOYEE_ID) }))
        .sort((a, b) => b.matchPct - a.matchPct),
    []
  );

// Kept for effect-based consumers that want a stable "loaded" signal even
// though mock data resolves synchronously — mirrors the shape a real fetch
// hook would return (loading/error), so pages don't need to change later.
export const useMockAsync = (value) => {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    setState({ loading: false, data: value });
  }, [value]);
  return state;
};
